import { ProductSlider } from '../../ProductSlider/ProductSlider';
import { type SuggestedAction } from '../../../../types/ai';
import { CART_ADD_ACTION_PREFIX, PRODUCT_SELECT_ACTION_PREFIX } from '../constants';
import {
  buildFallbackProductActionButton,
  buildProductActionButtons,
  hasProductSizes,
} from '../actions/product-actions';
import type { ProductCardData } from '../types';

export const ProductCardsGrid = ({
  cards,
  cardActions,
  onActionClick,
  expectedCount,
  lang,
}: {
  cards: ProductCardData[];
  cardActions?: SuggestedAction[][];
  onActionClick?: (action: SuggestedAction) => void;
  expectedCount?: number;
  lang?: string;
}) => {
  if (cards.length === 0 && (!expectedCount || expectedCount <= 0)) return null;
  const products = cards.map((c, index) => {
    const matchedActions = cardActions?.[index] ?? [];
    const actionButtons = buildProductActionButtons(matchedActions, c.slot, c.id, lang);
    const hasCartButton = actionButtons.some((button) =>
      String(button.value).startsWith(CART_ADD_ACTION_PREFIX),
    );
    const hasSizes = hasProductSizes(c);
    const fallbackActionButton =
      !hasCartButton && hasSizes
        ? buildFallbackProductActionButton(c.id, c.slot, true, lang)
        : actionButtons.length === 0
          ? buildFallbackProductActionButton(c.id, c.slot, false, lang)
          : undefined;

    const product = {
      id: c.id,
      selectionKey: c.id || c.url || `${c.brand ?? ''}:${c.title}`,
      name: c.title,
      brand: c.brand,
      reason: c.reason,
      price: c.price,
      currency: '',
      regularPrice: c.regularPrice,
      discountPercent: c.discountPercent,
      image: c.image,
      url: c.url,
      size: c.sizes,
      sizes: c.sizes,
      color: c.color,
      material: c.material,
      slot: c.slot,
      tryOn: c.tryOn,
      tryOnFraming: c.tryOnFraming,
      actionButtons: fallbackActionButton
        ? [...actionButtons, fallbackActionButton]
        : actionButtons.length > 0
          ? actionButtons
          : undefined,
    };
    return product;
  });
  const handleCardAction = (value: string) => {
    const flattenedCardActions = (cardActions ?? []).flat();
    const fromSuggested = flattenedCardActions.find((a) => a.value === value);
    if (fromSuggested) {
      onActionClick?.(fromSuggested);
      return;
    }

    if (value.startsWith(CART_ADD_ACTION_PREFIX)) {
      const baseValue = value.split('?')[0];
      const matched = flattenedCardActions.find(
        (action) => action.value === baseValue || action.actionId === baseValue,
      );
      onActionClick?.({
        ...(matched ?? { label: 'Lägg i varukorg', intentType: 'product_catalog' }),
        value,
        actionId: baseValue,
      });
      return;
    }
    const cardLabel = products
      .flatMap((p) => p.actionButtons ?? [])
      .find((button) => button.value === value)?.label;
    if (value.startsWith(PRODUCT_SELECT_ACTION_PREFIX)) {
      onActionClick?.({
        label: cardLabel ?? 'V\u00e4lj',
        value: cardLabel ?? 'V\u00e4lj',
        actionId: value,
      });
      return;
    }

    onActionClick?.({ label: cardLabel ?? value, value });
  };
  return (
    <ProductSlider
      products={products}
      onActionClick={handleCardAction}
      expectedCount={expectedCount}
      lang={lang}
    />
  );
};
